import { createContext, useContext, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { AsaHoja } from './AsaHoja';
import { useCerrarConAtras } from './atras';
import { color, movimiento, radio, sombra } from './tokens';
import { useCajaVisible } from './ventanaVisible';

/** Por encima de la barra de pestañas y del teclado de asignar. */
export const HOJA_Z_INDEX = 205;

/**
 * Lo pone `Modal` con su capa: una hoja abierta desde una ventana (p. ej.
 * «Bajar del barco» en «Compartir») sale encima de ella y cuenta como parte
 * de la ventana. Sin esto salía debajo, y tocarla cerraba la ventana sin
 * hacer nada.
 */
export const CapaVentanaContext = createContext<number | null>(null);

const sube = keyframes({
  from: { transform: 'translateY(100%)' },
  to: { transform: 'translateY(0)' },
});
const aparece = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });

/**
 * La hoja es una columna flex con scroll: sin esto, cuando el contenido no
 * cabe, los hijos se encogen y las filas se pisan (texto superpuesto).
 */
const sinEncoger = css({ '& > *': { flexShrink: 0 } });

type HojaProps = {
  'aria-label': string;
  'data-testid'?: string;
  onCerrar: () => void;
  /**
   * Alto fijo (p. ej. «90%»): la hoja no crece con el contenido y el scroll
   * lo lleva el contenido (la hoja de selección, con su buscador fijo).
   */
  alto?: string;
  children: ReactNode;
};

/**
 * Hoja inferior genérica: fondo oscurecido (tocarlo cierra), asa y contenido
 * con scroll. Entra deslizándose desde abajo; se cierra bajándola por el asa
 * o con «atrás».
 */
export function Hoja({
  'aria-label': ariaLabel,
  'data-testid': testId,
  onCerrar,
  alto,
  children,
}: HojaProps) {
  // «Atrás» del móvil cierra la hoja (la de arriba si hay varias).
  useCerrarConAtras(true, onCerrar);
  // Solo cierra un toque que empieza en el velo: el «click» que el móvil
  // manda tras el toque que abrió la hoja (sobre un campo que ahora tapa el
  // velo) no la cierra al instante.
  const empezoEnVelo = useRef(false);
  // Con el teclado del móvil abierto, la hoja se apoya encima del teclado en
  // vez de quedarse escondida debajo (iPhone).
  const caja = useCajaVisible();
  const capaVentana = useContext(CapaVentanaContext);
  const zIndex = capaVentana === null ? HOJA_Z_INDEX : capaVentana + 1;
  return createPortal(
    <View
      // La ventana no la toma por un toque fuera ni la esconde (react-aria).
      data-react-aria-top-layer={capaVentana === null ? undefined : true}
      style={{
        position: 'fixed',
        ...(caja
          ? { top: caja.arriba, left: 0, right: 0, height: caja.alto }
          : { inset: 0 }),
        zIndex,
        backgroundColor: color.veloHoja,
        justifyContent: 'flex-end',
        animation: `${aparece} ${movimiento.fondo}ms ease-out`,
      }}
      onPointerDown={e => {
        empezoEnVelo.current = e.target === e.currentTarget;
      }}
      onClick={e => {
        if (e.target === e.currentTarget && empezoEnVelo.current) {
          onCerrar();
        }
      }}
    >
      <View
        role="dialog"
        aria-modal
        aria-label={ariaLabel}
        data-testid={testId}
        className={alto ? undefined : sinEncoger}
        style={{
          maxHeight: '90%',
          overflowY: 'auto',
          backgroundColor: color.surfaceHoja,
          borderTop: color.bordeHoja,
          borderRadius: `${radio.heroe}px ${radio.heroe}px 0 0`,
          boxShadow: sombra.hoja,
          padding: '8px 16px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          animation: `${sube} ${movimiento.hoja}ms ${movimiento.suave}`,
          ...(alto && {
            height: alto,
            overflowY: 'hidden',
            paddingBottom: 0,
          }),
        }}
      >
        <AsaHoja onCerrar={onCerrar} />
        {children}
      </View>
    </View>,
    document.body,
  );
}
